import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";

import PatientIcon from "../assets/icon/PatientIcon";
import { useAuth } from "../context/AuthContext";
import { getContextAwareToken } from "../utils/storageHelper";
import PatientTable from "./Patient/PatientTable";

/* ---------------- AGE HELPER ---------------- */
const calculateAge = (birthDate?: string) => {
  if (!birthDate) return "--";
  const dob = new Date(birthDate);
  const diff = Date.now() - dob.getTime();
  return new Date(diff).getUTCFullYear() - 1970;
};

export const PatientLayout = () => {
  const { user } = useAuth();
  const token = getContextAwareToken(user?.token, user?.role as any);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);

  /* 🔑 REFRESH KEY */
  const [refreshKey, setRefreshKey] = useState(0);

  /* 🔔 ALERT STATE */
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

  /* ---------------- FETCH PATIENT LIST ---------------- */
  useEffect(() => {
    if (!open) return;

    const fetchPatients = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${BASE_URL}/patients`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json();
        setPatients(json?.data?.data || []);
      } catch (err) {
        console.error("Error fetching patients:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [open, token]);

  /* ---------------- SEARCH FILTER ---------------- */
  const filteredPatients = patients.filter((p) => {
    const name = `${p.first_name} ${p.last_name}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  /* ---------------- SELECT PATIENT ---------------- */
  const handleToggle = (patient_id: string) => {
    setSelectedPatients((prev) =>
      prev.includes(patient_id)
        ? prev.filter((x) => x !== patient_id)
        : [...prev, patient_id]
    );
  };

  /* ---------------- MAP PATIENTS ---------------- */
  const handleConfirm = async () => {
    if (!selectedPatients.length) {
      setAlert({
        open: true,
        message: "Please select at least one patient.",
        severity: "warning",
      });
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/patients/map-to-provider`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          provider_id: user?.id,
          patient_ids: selectedPatients,
        }),
      });

      if (!res.ok) throw new Error("Failed to map patients");

      setAlert({
        open: true,
        message: "Patients mapped successfully!",
        severity: "success",
      });

      setOpen(false);
      setSelectedPatients([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err: any) {
      setAlert({
        open: true,
        message: err.message || "Something went wrong",
        severity: "error",
      });
    }
  };

  return (
    <>
      {/* ================= PAGE HEADER ================= */}
      <div className="section" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <div className="heading">Patient</div>

          <button className="success-bttn" onClick={() => setOpen(true)}>
            <PatientIcon width={24} height={24} /> Add Patient
          </button>
        </div>

        <PatientTable refreshKey={refreshKey} />
      </div>

      {/* ================= MUI POPUP ================= */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Select Patients</DialogTitle>

        <DialogContent dividers>
          <input
            placeholder="Search patient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              marginBottom: 16,
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />

          {loading ? (
            <p>Loading...</p>
          ) : (
            <table width="100%">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Select</th>
                  <th style={{ textAlign: "left" }}>Name</th>
                  <th style={{ textAlign: "left" }}>Age</th>
                  <th style={{ textAlign: "left" }}>Gender</th>
                  <th style={{ textAlign: "left" }}>Contact</th>
                </tr>
              </thead>

              <tbody>
                {filteredPatients.map((p) => {
                  const isAlreadyAdded = p.current_provider === user?.id;

                  return (
                    <tr
                      key={p.patient_id}
                      style={{
                        opacity: isAlreadyAdded ? 0.5 : 1,
                        backgroundColor: isAlreadyAdded
                          ? "#f9fafb"
                          : "transparent",
                      }}
                    >
                      <td>
                        <Checkbox
                          checked={
                            isAlreadyAdded ||
                            selectedPatients.includes(p.patient_id)
                          }
                          disabled={isAlreadyAdded}
                          onChange={() => handleToggle(p.patient_id)}
                        />
                      </td>

                      <td>{p.first_name} {p.last_name}</td>
                      <td>{calculateAge(p.birth_date)}</td>
                      <td>{p.gender || "N/A"}</td>
                      <td>{p.contact_number || "N/A"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= THEMED ALERT ================= */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};
